import { Box } from '@primer/react';
import { ModelEntityType } from '@upright/common/model/input/types';
import { Link } from '@upright/ui/common/generic/components/primer-custom/link';
import { FontWeight } from '@upright/ui/common/generic/components/primer-custom/link/types';
import { useNetImpactLensContext } from '@upright/ui/common/upright/core/impact-lenses/net-impact/common/use-net-impact-lens-context';
import { getModelEntityUrl } from '@upright/ui/untidy/urls';
import Skeleton from 'react-loading-skeleton';
import { useTopContributors } from '@upright/ui/api/model/output/scores';
import { safeAssert } from '@upright/common/safe-assert';
import { useRouteParams } from '@upright/ui/common/generic/hooks/url-handling/route-params';
import { useGetGroupfolioQuery } from '@upright/ui/api/model/input/company-groups/hooks';
import {
  AccessLevel,
  isEnoughAccessToReadOutput,
  isEnoughAccessToReadWithoutWeights,
} from '@upright/common/iam/access/types';
import { SpecialDataSourceId } from '@upright/common/model/input/data-sources/types';

interface ItemContribution {
  name: string;
  id: string;
  entityType: ModelEntityType;
  accessLevel?: AccessLevel | null | undefined;
}

export function WeightSetItemContributionsList() {
  const context = useNetImpactLensContext();

  const groupId = useRouteParams<{ id: string }>().id;
  const groupQuery = useGetGroupfolioQuery(groupId);
  const showHoldings = (() => {
    if (!groupQuery.isSuccess) {
      return false;
    }
    if (isEnoughAccessToReadWithoutWeights(groupQuery.data.accessLevel)) {
      return true;
    }
    if (
      isEnoughAccessToReadOutput(groupQuery.data.accessLevel) &&
      groupQuery.data.source === SpecialDataSourceId.FINNHUB
    ) {
      return true;
    }
    return false;
  })();

  const selection = context?.state;
  const impact = selection?.impact;
  const valence = selection?.valence;

  safeAssert(valence, 'Contributor lists was rendered with unknown valence');

  const topContributorsQuery = useTopContributors(
    context?.data?.entity?.type,
    context?.data?.modelResultId,
    impact,
    valence,
    {
      limit: 5,
      enabled: showHoldings,
    },
  );

  if (!showHoldings) {
    return <></>;
  }

  if (!context?.data || topContributorsQuery.isLoading) {
    return <Skeleton width="10em" />;
  }

  if (!topContributorsQuery.data) {
    return <></>;
  }

  const contributionList: ItemContribution[] = topContributorsQuery.data.contributors.flatMap(item => {
    if (item.entityId && item.entityType && item.label) {
      return [
        {
          id: item.entityId,
          entityType: item.entityType,
          name: item.label,
          accessLevel: item.accessLevel,
        },
      ];
    }
    return [];
  });

  const items = contributionList.map((d, index) => {
    const entityType = d.entityType;
    const showLink = !context.options.disableLinks && isEnoughAccessToReadWithoutWeights(d.accessLevel);
    return (
      <Box as="li" key={index} sx={{ fontWeight: '500', mt: 1 }}>
        {showLink ? (
          <Link indicator="underline" fontWeight={FontWeight.SEMIBOLD} to={getModelEntityUrl(entityType, d.id, d.name)}>
            {d.name}
          </Link>
        ) : (
          d.name
        )}
      </Box>
    );
  });

  if (items.length === 1) {
    return <div>Top contributor: {items}</div>;
  } else if (items.length > 1) {
    return (
      <Box sx={{ color: 'text.muted' }}>
        <Box sx={{ fontWeight: 'bold', mb: 2 }}>Top contributors</Box>
        <Box as="ul" sx={{ marginInlineStart: '1.75em !important;' }}>
          {items}
        </Box>
      </Box>
    );
  } else {
    /* Show nothing on error */
    return <></>;
  }
}
